import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { find, omit } from 'lodash';
import Select from '../util/Select';
import IconLabel from '../util/IconLabel';
import { defineMessages, intlShape, injectIntl } from 'react-intl';
import qs from 'qs';
import { selectIsReverseOrder } from '../../selectors/filteredList';

import styles from './SortSelect.css';

const messages = defineMessages({
  alphabetical: {
    id: 'recording-sort.alphabetical',
    defaultMessage: 'a-z',
  },
  chronological: {
    id: 'recording-sort.chronological',
    defaultMessage: 'Date',
  },
  popular: {
    id: 'recording-sort.popularity',
    defaultMessage: 'popularity',
  },
  recent: {
    id: 'recording-sort.recent',
    defaultMessage: 'recently added',
  },
  relevance: {
    id: 'recording-sort.relevance',
    defaultMessage: 'relevance',
  },
  sort: {
    id: 'recording-sort.sort',
    defaultMessage: 'sort',
  },
  reverse: {
    id: 'recording-sort.reverse',
    defaultMessage: 'Reverse order: {direction}',
  },
  mostPopular: {
    id: 'recording-sort.direction.most-popular',
    defaultMessage: 'Most popular first',
  },
  leastPopular: {
    id: 'recording-sort.direction.least-popular',
    defaultMessage: 'Least popular first',
  },
  newestAdded: {
    id: 'recording-sort.direction.newest-added',
    defaultMessage: 'Newest added first',
  },
  oldestAdded: {
    id: 'recording-sort.direction.oldest-added',
    defaultMessage: 'Oldest added first',
  },
  newestRecording: {
    id: 'recording-sort.direction.newest-recording',
    defaultMessage: 'Newest recording first',
  },
  oldestRecording: {
    id: 'recording-sort.direction.oldest-recording',
    defaultMessage: 'Oldest recording first',
  },
  mostRelevant: {
    id: 'recording-sort.direction.most-relevant',
    defaultMessage: 'Most relevant first',
  },
  leastRelevant: {
    id: 'recording-sort.direction.least-relevant',
    defaultMessage: 'Least relevant first',
  },
  aToZ: {
    id: 'recording-sort.direction.a-to-z',
    defaultMessage: 'A to Z',
  },
  zToA: {
    id: 'recording-sort.direction.z-to-a',
    defaultMessage: 'Z to A',
  },
  newestRelease: {
    id: 'recording-sort.direction.newest-release',
    defaultMessage: 'Newest release first',
  },
  oldestRelease: {
    id: 'recording-sort.direction.oldest-release',
    defaultMessage: 'Oldest release first',
  },
  mostRecorded: {
    id: 'recording-sort.direction.most-recorded',
    defaultMessage: 'Most recorded first',
  },
  leastRecorded: {
    id: 'recording-sort.direction.least-recorded',
    defaultMessage: 'Least recorded first',
  },
});

const alphabetical = {
  value: 'alphabetical',
  text: messages.alphabetical,
  isAscending: true,
  orders: [messages.aToZ, messages.zToA],
};

const sortOptions = {
  recordings: [
    {
      value: 'popular',
      text: messages.popular,
      orders: [messages.mostPopular, messages.leastPopular],
    },
    {
      value: 'recent',
      text: messages.recent,
      orders: [messages.newestAdded, messages.oldestAdded],
    },
    {
      value: 'chronological',
      text: messages.chronological,
      orders: [messages.newestRecording, messages.oldestRecording],
    },
  ],
  albums: [
    {
      value: 'relevance',
      text: messages.relevance,
      orders: [messages.mostRelevant, messages.leastRelevant],
    },
    alphabetical,
    {
      value: 'publishDate',
      text: messages.chronological,
      orders: [messages.newestRelease, messages.oldestRelease],
    },
  ],
  works: [
    {
      value: 'popular',
      text: messages.popular,
      orders: [messages.mostRecorded, messages.leastRecorded],
    },
    alphabetical,
  ],
};

class SortSelect extends Component {
  static propTypes = {
    sort: PropTypes.string,
    location: PropTypes.object,
    currentCursor: PropTypes.string,
    optionsContext: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
    intl: intlShape,
  };

  static contextTypes = {
    router: PropTypes.object,
  };

  getDirectionProps(option) {
    const { formatMessage } = this.props.intl;
    const isReversed = selectIsReverseOrder(this.props.location.query);
    const [order, oppositeOrder] = isReversed ? [...option.orders].reverse() : option.orders;
    const isAscending = !!option.isAscending !== isReversed;

    return {
      onReselect: this.reverseSorting,
      currentOptionIcon: (
        <IconLabel
          name="sort-arrow"
          size="0.75rem"
          className={isAscending ? styles.ascending : undefined}
          data-test="sort-select.direction"
          data-direction={isAscending ? 'ascending' : 'descending'}
        />
      ),
      currentOptionDescription: formatMessage(order),
      reselectDescription: formatMessage(messages.reverse, {
        direction: formatMessage(oppositeOrder),
      }),
    };
  }

  render() {
    const { optionsContext, sort } = this.props;
    const { formatMessage } = this.props.intl;
    const options = sortOptions[optionsContext];
    const currentOption = find(options, { value: sort });

    return (
      <Select
        optionsDescriptor={formatMessage(messages.sort)}
        defaultOptionValue="chronological"
        currentOptionValue={sort}
        options={options.map(({ value, text }) => ({ value, text: formatMessage(text) }))}
        onSelect={this.applySorting}
        isDisabled={this.props.isDisabled}
        {...(currentOption && this.getDirectionProps(currentOption))}
      />
    );
  }

  applySorting = sort => this.pushSortingToRouter(sort, false);

  reverseSorting = sort =>
    this.pushSortingToRouter(sort, !selectIsReverseOrder(this.props.location.query));

  pushSortingToRouter(sort, reverseOrder) {
    const { currentCursor, location } = this.props;
    const query = { ...omit(location.query, 'reverseOrder'), sort };
    if (reverseOrder) {
      query.reverseOrder = true;
    }

    let { pathname } = location;
    if (currentCursor) {
      // Always make sure to remove the cursor when changing sorting
      pathname = pathname.replace(`/pages/${currentCursor}`, '');
    }
    const target = pathname + '?' + qs.stringify(query);
    this.context.router.push(target);
  }
}

export default injectIntl(SortSelect);
